import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import TimeAddOn from "@coworker/apprestructured/src/task-management/components/LogsAndComments/TimeAddOn/TimeAddOn";
import styles from "./CommentsTab.module.css";
import { CommentImages } from "@coworker/apprestructured/src/task-management/components/CommentImages/CommentImages";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteComment } from "../../../services/useDeleteComment";
import { useUpdateComment } from "../../../services/useCreateComment";
import { useTranslation } from "@coworker/locales";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import { ConfirmDeleteCommentPrompt } from "./ConfirmDeleteCommentPrompt";
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath";
import { AddOrEditComment } from "@coworker/apprestructured/src/task-management/components/AddOrEditComment/AddOrEditComment";
import {
  ExistingFile,
  filesToNewFiles,
  NewOrExistingFile,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import {
  getTaskCommentsImagesQueryKey,
  getTaskCommentsQueryKey,
} from "../../../services/queryKeyHelper";
import { Comment } from "../../../types/Comment";

type CommentsTabProps = {
  taskId: string;
  items: CommentsTabItem[];
};

export type CommentsTabItem = {
  id: string;
  title: string;
  content: string;
  time: string;
  imageUrls: string[] | Loading;
  imageCreateUploads: {
    name: string;
    size: number;
    bucket_id: string;
  }[];
  creatorUserId: string;
};

function CommentsTab({ taskId, items }: CommentsTabProps) {
  const queryClient = useQueryClient();
  const uid = useUID();
  const { t } = useTranslation();
  const { mutate: deleteComment, isPending: isDeletingComment } =
    useDeleteComment();
  const { mutateAsync: updateCommentMutation, isPending: isUpdatingComment } =
    useUpdateComment();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState<string | null>(
    null
  );
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [initialEditingText, setInitialEditingText] = useState("");
  const [editingAllImages, setEditingAllImages] = useState<NewOrExistingFile[]>(
    []
  );
  const [initialEditingExistingImageIds, setInitialEditingExistingImageIds] =
    useState<string[]>([]);
  const [editedComments, setEditedComments] = useState<Record<string, string>>(
    {}
  );

  const editingImages = useMemo(() => {
    return editingAllImages
      .filter(
        (f): f is Extract<NewOrExistingFile, { type: "new" }> =>
          f.type === "new"
      )
      .map(({ file }) => file);
  }, [editingAllImages]);

  const editingExistingImages = useMemo(() => {
    return editingAllImages.filter(
      (f): f is ExistingFile => f.type === "existing"
    );
  }, [editingAllImages]);

  const editingImagesForImageUploader = useMemo(() => {
    return filesToNewFiles(editingImages);
  }, [editingImages]);

  const {
    ready: editingImageUploaderIsReady,
    createUploads: editingImageCreateUploads,
    upload: uploadEditingImages,
  } = useImageUploader(editingImagesForImageUploader);

  const handleDeleteClick = useCallback((commentId: string) => {
    setCommentIdToDelete(commentId);
    setShowDeleteConfirmation(true);
  }, []);

  const handleConfirmClick = useCallback(() => {
    if (commentIdToDelete) {
      deleteComment({ taskId, commentId: commentIdToDelete });
      setShowDeleteConfirmation(false);
      setCommentIdToDelete(null);
    }
  }, [deleteComment, taskId, commentIdToDelete]);

  const handleCancelClick = useCallback(() => {
    setShowDeleteConfirmation(false);
    setCommentIdToDelete(null);
  }, []);

  const handleEditClick = useCallback(
    (
      commentId: string,
      content: string,
      imageUrls: string[] | Loading,
      imageCreateUploads: CommentsTabItem["imageCreateUploads"]
    ) => {
      setEditingCommentId(commentId);
      setEditingText(content);
      setInitialEditingText(content);

      const existingImages: ExistingFile[] = Array.isArray(imageUrls)
        ? imageCreateUploads
            .map((imageCreateUpload, index) => {
              const url = imageUrls[index];
              if (!url) {
                return null;
              }
              return {
                type: "existing" as const,
                rotation: 0,
                file: {
                  id: imageCreateUpload.bucket_id,
                  url,
                  name: imageCreateUpload.name,
                  size: imageCreateUpload.size,
                },
              };
            })
            .filter((image): image is ExistingFile => image !== null)
        : [];

      setEditingAllImages(existingImages);
      setInitialEditingExistingImageIds(
        existingImages.map(({ file }) => file.id)
      );
    },
    []
  );

  const handleSaveClick = useCallback(() => {
    async function fn() {
      if (!editingCommentId) {
        return;
      }

      const currentExistingImageIds = editingExistingImages.map(
        ({ file }) => file.id
      );
      const hasTextChanges = editingText !== initialEditingText;
      const hasImageChanges =
        editingImages.length > 0 ||
        currentExistingImageIds.length !==
          initialEditingExistingImageIds.length ||
        currentExistingImageIds.some(
          (id, index) => id !== initialEditingExistingImageIds[index]
        ) ||
        editingAllImages.length !== initialEditingExistingImageIds.length;

      if (!hasTextChanges && !hasImageChanges) {
        setEditingCommentId(null);
        setEditingAllImages([]);
        setInitialEditingText("");
        setInitialEditingExistingImageIds([]);
        return;
      }

      const updates = {
        ...(hasTextChanges ? { text: editingText } : {}),
        ...(hasImageChanges
          ? {
              images: (() => {
                let newIdx = 0;
                return editingAllImages
                  .map((image) => {
                    if (image.type === "existing") {
                      return {
                        name: image.file.name,
                        size: image.file.size,
                        bucket_id: image.file.id,
                      };
                    }
                    return editingImageCreateUploads[newIdx++];
                  })
                  .filter((x): x is NonNullable<typeof x> => x !== undefined);
              })(),
            }
          : {}),
      };

      const [updateCommentResult, uploadResult] = await Promise.allSettled([
        updateCommentMutation({
          taskId,
          commentId: editingCommentId,
          updates,
        }),
        ...(editingImages.length > 0 ? [uploadEditingImages()] : []),
      ]);

      if (
        updateCommentResult.status === "fulfilled" &&
        (editingImages.length === 0 || uploadResult?.status === "fulfilled")
      ) {
        queryClient.setQueryData<Comment[]>(
          getTaskCommentsQueryKey(taskId),
          (prevComments) => {
            if (!prevComments) {
              return prevComments;
            }

            return prevComments.map((comment) =>
              comment.id === editingCommentId
                ? updateCommentResult.value
                : comment
            );
          }
        );

        if (hasImageChanges) {
          await queryClient.invalidateQueries({
            queryKey: getTaskCommentsImagesQueryKey(taskId),
          });
        }

        setEditedComments((prev) => {
          const next = { ...prev };
          delete next[editingCommentId];
          return next;
        });
        setEditingCommentId(null);
        setEditingAllImages([]);
        setInitialEditingText("");
        setInitialEditingExistingImageIds([]);
      }
    }

    fn();
  }, [
    editingAllImages,
    editingCommentId,
    editingExistingImages,
    editingImageCreateUploads,
    editingImages,
    editingText,
    initialEditingExistingImageIds,
    initialEditingText,
    queryClient,
    taskId,
    updateCommentMutation,
    uploadEditingImages,
  ]);

  return (
    <>
      <FixaListView id="comments">
        {items.map(
          (
            {
              id,
              title,
              content,
              time,
              imageUrls,
              imageCreateUploads,
              creatorUserId,
            },
            i
          ) => (
            <FixaListViewItem
              key={i}
              className={styles["item"]}
              title={title}
              description={
                <div className={styles["description"]}>
                  {editingCommentId === id ? (
                    <AddOrEditComment
                      text={editingText}
                      images={editingImages}
                      existingImages={editingExistingImages}
                      disableAllInputs={isUpdatingComment}
                      disableAddButton={
                        isUpdatingComment ||
                        (editingImages.length > 0 &&
                          !editingImageUploaderIsReady)
                      }
                      label={t("editCommentString")}
                      buttonText={t("saveString")}
                      onTextChange={setEditingText}
                      onImagesChange={() => {}}
                      onAllImagesChange={setEditingAllImages}
                      onAddClick={handleSaveClick}
                    />
                  ) : (
                    <>
                      {(editedComments[id] ?? content).length > 0
                        ? (editedComments[id] ?? content)
                        : null}
                      <CommentImages urls={imageUrls} />
                    </>
                  )}
                </div>
              }
              addOn={
                <div className={styles["addOn"]}>
                  <TimeAddOn>{time}</TimeAddOn>
                  {uid === creatorUserId && editingCommentId === null && (
                    <div>
                      <FixaButton
                        iconOnly
                        size="xsmall"
                        type="tertiary"
                        onClick={() =>
                          handleEditClick(
                            id,
                            editedComments[id] ?? content,
                            imageUrls,
                            imageCreateUploads
                          )
                        }
                        disabled={isDeletingComment || isLoading(imageUrls)}
                        ssrIcon={PencilIconPath}
                        className={styles["editButton"]}
                        aria-label={t("editString")}
                      />
                      <FixaButton
                        iconOnly
                        size="xsmall"
                        type="tertiary"
                        onClick={() => handleDeleteClick(id)}
                        disabled={isDeletingComment}
                        ssrIcon={TrashCanIconPath}
                        aria-label={t("deleteString")}
                      />
                    </div>
                  )}
                </div>
              }
              emphasised
              inset
            />
          )
        )}
      </FixaListView>
      <ConfirmDeleteCommentPrompt
        visible={showDeleteConfirmation}
        disabled={isDeletingComment}
        onCancel={handleCancelClick}
        onConfirm={handleConfirmClick}
      />
    </>
  );
}

export default CommentsTab;
